import { useEffect, useState } from "react";
import { Loader2, Radar, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";

export default function SubdomainDialog({ websiteId, open, onClose, onAdded }) {
  const [loading, setLoading] = useState(true);
  const [root, setRoot] = useState("");
  const [subs, setSubs] = useState([]);
  const [selected, setSelected] = useState({});
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected({});
    (async () => {
      try {
        const { data } = await api.get(`/websites/${websiteId}/subdomains`);
        setRoot(data.root);
        setSubs(data.subdomains);
        // pre-select resolvable + not yet monitored
        const pre = {};
        data.subdomains.forEach((s) => {
          if (s.resolves && !s.already_monitored) pre[s.subdomain] = true;
        });
        setSelected(pre);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Discovery failed");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [open, websiteId]); // eslint-disable-line

  const toggle = (name) => setSelected((p) => ({ ...p, [name]: !p[name] }));
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const addSelected = async () => {
    const urls = subs.filter((s) => selected[s.subdomain] && !s.already_monitored).map((s) => s.url);
    if (urls.length === 0) return;
    setAdding(true);
    try {
      const { data } = await api.post("/websites/bulk", { urls });
      toast.success(`Added ${data.added.length} monitor${data.added.length === 1 ? "" : "s"}` +
        (data.skipped.length ? ` · ${data.skipped.length} skipped` : ""));
      onAdded?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk add failed");
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="subdomain-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Radar size={18} /> Discover subdomains</DialogTitle>
          <DialogDescription>
            {loading ? "Scanning certificate transparency logs & DNS…" : `Found ${subs.length} names for ${root}. Select which to monitor.`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
        ) : (
          <div className="max-h-[50vh] space-y-1 overflow-y-auto pr-1" data-testid="subdomain-list">
            {subs.map((s) => (
              <label
                key={s.subdomain}
                className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                  s.already_monitored ? "border-slate-100 bg-slate-50 opacity-70" : "border-slate-200 hover:bg-slate-50 cursor-pointer"
                }`}
                data-testid="subdomain-item"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Checkbox
                    checked={s.already_monitored || !!selected[s.subdomain]}
                    disabled={s.already_monitored}
                    onCheckedChange={() => toggle(s.subdomain)}
                    data-testid={`subdomain-check-${s.subdomain}`}
                  />
                  <span className="truncate font-mono text-sm text-slate-800">{s.subdomain}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {s.already_monitored ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-mono text-blue-700"><Check size={10} /> monitored</span>
                  ) : s.resolves ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-mono text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> resolves</span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-400">no DNS</span>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button onClick={addSelected} disabled={adding || loading || selectedCount === 0} data-testid="add-subdomains-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {adding ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-1.5" />}
            Add {selectedCount} monitor{selectedCount === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
